import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Box,
  Chip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Folder as FolderIcon,
  InsertDriveFile as FileIcon,
  Code as CodeIcon,
  Description as DocIcon,
  BugReport as TestIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTDD } from '../../context/TDDContext';
import TDDService from '../../services/tddService';

/**
 * TDD Workspace View Component
 * Shows the workspace structure and generated files
 */
const TDDWorkspaceView = ({ projectId }) => {
  const { tddState, updateWorkspace } = useTDD();
  
  const [expandedFolders, setExpandedFolders] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState('');
  const [showFileDialog, setShowFileDialog] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Organize files into a tree structure
   */
  const organizeFiles = (files) => {
    const tree = {};
    
    files.forEach(file => {
      const parts = file.path.split('/');
      let current = tree;
      
      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          // This is a file
          current[part] = {
            type: 'file',
            path: file.path,
            size: file.size,
            lastModified: file.lastModified,
            extension: part.split('.').pop()
          };
        } else {
          // This is a folder
          if (!current[part]) {
            current[part] = {
              type: 'folder',
              children: {}
            };
          }
          current = current[part].children;
        }
      });
    });
    
    return tree;
  };

  /**
   * Get file icon based on extension
   */
  const getFileIcon = (extension) => {
    switch (extension?.toLowerCase()) {
      case 'js':
      case 'ts':
      case 'jsx':
      case 'tsx':
      case 'py':
      case 'java':
      case 'cpp':
      case 'c':
        return <CodeIcon color="primary" />;
      case 'test':
      case 'spec':
        return <TestIcon color="secondary" />;
      case 'md':
      case 'txt':
      case 'doc':
        return <DocIcon color="info" />;
      default:
        return <FileIcon />;
    }
  };

  /**
   * Get file type color
   */
  const getFileTypeColor = (extension) => {
    switch (extension?.toLowerCase()) {
      case 'js':
      case 'jsx':
        return 'warning';
      case 'ts':
      case 'tsx':
        return 'info';
      case 'py':
        return 'success';
      case 'test':
      case 'spec':
        return 'secondary';
      case 'md':
        return 'primary';
      default:
        return 'default';
    }
  };

  /**
   * Toggle folder expansion
   */
  const toggleFolder = (folderName) => {
    setExpandedFolders(prev => ({
      ...prev,
      [folderName]: !prev[folderName]
    }));
  };

  /**
   * View file content
   */
  const viewFile = async (file) => {
    setSelectedFile(file);
    try {
      const response = await TDDService.getFileContent(projectId, file.path);
      if (response.success) {
        setFileContent(response.content || '// File is empty');
      } else {
        setFileContent(`// Error loading file: ${response.message || 'Unknown error'}`);
      }
      setShowFileDialog(true);
    } catch (error) {
      console.error('Failed to load file content:', error);
      setFileContent(`// Error loading file content: ${error.message}`);
      setShowFileDialog(true);
    }
  };

  /**
   * Refresh workspace
   */
  const refreshWorkspace = async () => {
    setIsRefreshing(true);
    try {
      const response = await TDDService.getWorkspaceFiles(projectId);
      if (response.success) {
        updateWorkspace({
          files: response.files || [],
          lastUpdated: new Date().toISOString()
        });
      }
    } catch (error) {
      console.error('Failed to refresh workspace:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Render file tree recursively
   */
  const renderFileTree = (tree, level = 0) => {
    return Object.entries(tree).map(([name, item]) => (
      <React.Fragment key={name}>
        <ListItem
          sx={{
            pl: 2 + level * 2,
            cursor: item.type === 'folder' ? 'pointer' : 'default',
            '&:hover': { backgroundColor: '#f5f5f5' }
          }}
          onClick={item.type === 'folder' ? () => toggleFolder(name) : undefined}
        >
          <ListItemIcon sx={{ minWidth: 35 }}>
            {item.type === 'folder' ? (
              <FolderIcon color="action" />
            ) : (
              getFileIcon(item.extension)
            )}
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">{name}</Typography>
                {item.type === 'file' && item.extension && (
                  <Chip
                    label={item.extension}
                    size="small"
                    color={getFileTypeColor(item.extension)}
                    variant="outlined"
                  />
                )}
              </Box>
            }
            secondary={
              item.type === 'file' && item.size ? (
                <Typography variant="caption" color="text.secondary">
                  {item.size} bytes
                </Typography>
              ) : null
            }
          />
          {item.type === 'folder' && (
            <IconButton size="small">
              {expandedFolders[name] ? <CollapseIcon /> : <ExpandIcon />}
            </IconButton>
          )}
          {item.type === 'file' && (
            <IconButton size="small" onClick={() => viewFile(item)}>
              <ViewIcon />
            </IconButton>
          )}
        </ListItem>
        
        {item.type === 'folder' && expandedFolders[name] && (
          <Collapse in={expandedFolders[name]} timeout="auto" unmountOnExit>
            {renderFileTree(item.children, level + 1)}
          </Collapse>
        )}
      </React.Fragment>
    ));
  };

  // Organize workspace files
  const fileTree = tddState.workspace.files ? organizeFiles(tddState.workspace.files) : {};

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Workspace Files
          </Typography>
          <IconButton onClick={refreshWorkspace} disabled={isRefreshing}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Workspace Stats */}
        <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label={`${tddState.workspace.files?.length || 0} files`}
            size="small"
            color="primary"
            variant="outlined"
          />
          {tddState.workspace.lastUpdated && (
            <Chip
              label={`Updated: ${new Date(tddState.workspace.lastUpdated).toLocaleTimeString()}`}
              size="small"
              color="info"
              variant="outlined"
            />
          )}
        </Box>

        {/* File Tree */}
        {Object.keys(fileTree).length > 0 ? (
          <List dense sx={{ maxHeight: 400, overflow: 'auto' }}>
            {renderFileTree(fileTree)}
          </List>
        ) : (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography color="text.secondary">
              {tddState.workspace.status === 'ready' 
                ? 'No files in workspace yet'
                : 'Initialize workspace to see files'
              }
            </Typography>
          </Box>
        )}

        {/* TDD Specific Files Info */}
        {tddState.workspace.status === 'ready' && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>TDD Workflow Files:</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • <strong>context/</strong> - Project documentation and MD files<br />
              • <strong>prompts/</strong> - Ralphio TDD prompt templates<br />
              • <strong>tasks/</strong> - Task breakdown and progress tracking<br />
              • <strong>tests/</strong> - Generated test files<br />
              • <strong>src/</strong> - Generated implementation code
            </Typography>
          </Box>
        )}
      </CardContent>

      {/* File Content Dialog */}
      <Dialog
        open={showFileDialog}
        onClose={() => setShowFileDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedFile ? selectedFile.path : 'File Content'}
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              backgroundColor: '#f5f5f5',
              p: 2,
              borderRadius: 1,
              overflow: 'auto',
              maxHeight: 400,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap'
            }}
          >
            {fileContent}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFileDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default TDDWorkspaceView;